import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import {
  getAlertConfig,
  getAlertConfigHistory,
  patchMatrix,
  patchDnd,
  patchMorningSummary,
  patchWarnThresholds,
  createRecipient,
  updateRecipient,
  deleteRecipient,
  type AlertConfigResponse,
  type AlertConfigHistoryResponse,
  type MatrixBySeverity,
  type DndConfig,
  type MorningSummaryConfig,
  type WarnThresholdsConfig,
  type RecipientCreateBody,
  type RecipientPatchBody,
} from '../../api/alertConfigApi';

// 권위: docs/ARCH_DASHBOARD.md §4.9
// Query key 규약:
//   ['alert-config']            — 전체 설정 (matrix/dnd/summary/warn/recipients 포함)
//   ['alert-config-history', p] — history 페이지별
//
// 각 섹션 PATCH 는 서버가 최신 AlertConfigResponse 를 돌려주므로, onSuccess 에서
// setQueryData 로 즉시 덮어쓰고 history 만 invalidate 한다. (왕복 1회 절감)

export const QK_CONFIG = ['alert-config'] as const;
export const QK_HISTORY_PREFIX = ['alert-config-history'] as const;

export type AlertToastFn = (
  msg: string,
  tone?: 'info' | 'success' | 'error' | 'warn'
) => void;

// ── 조회 ────────────────────────────────────────────────

export function useAlertConfig() {
  return useQuery<AlertConfigResponse>({
    queryKey: QK_CONFIG,
    queryFn: getAlertConfig,
    staleTime: 30_000,
  });
}

export function useAlertConfigHistory(page: number, size: number) {
  return useQuery<AlertConfigHistoryResponse>({
    queryKey: [...QK_HISTORY_PREFIX, page, size],
    queryFn: () => getAlertConfigHistory(page, size),
    staleTime: 30_000,
  });
}

// ── 공통 에러 메시지 ────────────────────────────────────

function extractMsg(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: unknown }).message ?? '저장 실패');
  }
  return '저장 실패';
}

// 권위: dashboard-api 가 422 로 회신할 수 있는 수신자 관련 도메인 코드.
//   - recipient_duplicate : (channel, address) unique 충돌
//                            (PATCH 로 channel/address 변경 시에도 발생)
function recipientErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'recipient_duplicate') {
      return '이미 등록된 channel/address 조합입니다. 다른 값을 사용하세요.';
    }
  }
  return extractMsg(err);
}

// ── 변경 뮤테이션 ───────────────────────────────────────

export function useMatrixMutation(toast?: AlertToastFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matrix: MatrixBySeverity) => patchMatrix({ matrix }),
    onSuccess: (data) => {
      qc.setQueryData(QK_CONFIG, data);
      qc.invalidateQueries({ queryKey: QK_HISTORY_PREFIX });
      toast?.('매트릭스 저장됨', 'success');
    },
    onError: (err) => toast?.(extractMsg(err), 'error'),
  });
}

export function useDndMutation(toast?: AlertToastFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dnd: DndConfig) => patchDnd({ dnd }),
    onSuccess: (data) => {
      qc.setQueryData(QK_CONFIG, data);
      qc.invalidateQueries({ queryKey: QK_HISTORY_PREFIX });
      toast?.('DND 저장됨', 'success');
    },
    onError: (err) => toast?.(extractMsg(err), 'error'),
  });
}

export function useMorningSummaryMutation(toast?: AlertToastFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (morningSummary: MorningSummaryConfig) =>
      patchMorningSummary({ morningSummary }),
    onSuccess: (data) => {
      qc.setQueryData(QK_CONFIG, data);
      qc.invalidateQueries({ queryKey: QK_HISTORY_PREFIX });
      toast?.('아침 요약 저장됨', 'success');
    },
    onError: (err) => toast?.(extractMsg(err), 'error'),
  });
}

export function useWarnThresholdsMutation(toast?: AlertToastFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (warnThresholds: WarnThresholdsConfig) =>
      patchWarnThresholds({ warnThresholds }),
    onSuccess: (data) => {
      qc.setQueryData(QK_CONFIG, data);
      qc.invalidateQueries({ queryKey: QK_HISTORY_PREFIX });
      toast?.('WARN 에스컬레이션 저장됨', 'success');
    },
    onError: (err) => toast?.(extractMsg(err), 'error'),
  });
}

// ── 수신자 CRUD ─────────────────────────────────────────
// 수신자 변경은 AlertConfigResponse 전체를 돌려주지 않으므로 invalidateQueries.

export function useCreateRecipientMutation(toast?: AlertToastFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecipientCreateBody) => createRecipient(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_CONFIG });
      qc.invalidateQueries({ queryKey: QK_HISTORY_PREFIX });
      toast?.('수신자 추가됨', 'success');
    },
    onError: (err) => toast?.(recipientErrorMessage(err), 'error'),
  });
}

export function useUpdateRecipientMutation(toast?: AlertToastFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (arg: { recipientId: number; body: RecipientPatchBody }) =>
      updateRecipient(arg.recipientId, arg.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_CONFIG });
      qc.invalidateQueries({ queryKey: QK_HISTORY_PREFIX });
      toast?.('수신자 수정됨', 'success');
    },
    onError: (err) => toast?.(recipientErrorMessage(err), 'error'),
  });
}

export function useDeleteRecipientMutation(toast?: AlertToastFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recipientId: number) => deleteRecipient(recipientId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_CONFIG });
      qc.invalidateQueries({ queryKey: QK_HISTORY_PREFIX });
      toast?.('수신자 삭제됨', 'success');
    },
    onError: (err) => toast?.(extractMsg(err), 'error'),
  });
}
